import Api from '../api';
import { API_CONFIG } from '../config';

/**
 * Employee API Service
 * Handles all employee-related API calls
 */

const employeeService = {
  /**
   * Get all employees
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @param {string} params.$select - OData select fields
   * @param {string} params.$orderby - OData orderby clause
   * @param {number} params.$top - Limit number of results
   * @param {number} params.$skip - Skip number of results
   * @returns {Promise} Axios response with employee data
   */
  async getEmployees(params = {}) {
    console.log('🔍 Fetching employees with params:', params);

    // First try without cross-company to avoid SQL subquery errors
    try {
      const queryParams = {
        'cross-company': false,
        ...params,
      };

      const response = await Api.get('/data/EmployeesV2', { params: queryParams });

      if (response.data && response.data.value && response.data.value.length > 0) {
        console.log(`✅ Found ${response.data.value.length} employees (without cross-company)`);
        return response;
      }

      // If no results, try with cross-company
      console.log('⚠️ No results without cross-company, trying with cross-company=true');
      const queryParamsWithCrossCompany = {
        'cross-company': true,
        ...params,
      };

      return await Api.get('/data/EmployeesV2', { params: queryParamsWithCrossCompany });
    } catch (error) {
      // If we get a 400 error (SQL subquery issue), return empty result
      if (error.response && error.response.status === 400) {
        console.error(
          '❌ 400 Error - SQL subquery issue, returning empty result. Response body:',
          error.response.data
        );
        return { data: { value: [] } };
      }
      throw error;
    }
  },

  /**
   * Get employee by personnel number
   * @param {string} personnelNumber - Employee personnel number
   * @returns {Promise} Axios response with employee data
   */
  async getEmployeeByNumber(personnelNumber) {
    console.log(`🔍 Fetching employee by number: ${personnelNumber}`);

    // First try without cross-company to avoid SQL subquery errors
    try {
      const params = {
        $filter: `PersonnelNumber eq '${personnelNumber}'`,
        'cross-company': false,
      };

      const response = await Api.get('/data/EmployeesV2', { params });

      if (response.data && response.data.value && response.data.value.length > 0) {
        console.log('✅ Employee found (without cross-company)');
        return response;
      }

      // If no results, try with cross-company
      console.log('⚠️ No results without cross-company, trying with cross-company=true');
      const paramsWithCrossCompany = {
        $filter: `PersonnelNumber eq '${personnelNumber}'`,
        'cross-company': true,
      };

      return await Api.get('/data/EmployeesV2', { params: paramsWithCrossCompany });
    } catch (error) {
      // If we get a 400 error (SQL subquery issue), return empty result
      if (error.response && error.response.status === 400) {
        console.error(
          '❌ 400 Error - SQL subquery issue, returning empty result. Response body:',
          error.response.data
        );
        return { data: { value: [] } };
      }
      throw error;
    }
  },

  /**
   * Get employees whose Employment Start Date falls on or after the given date.
   *
   * Used by the Manager Dashboard "New Joiners" metric. The caller passes an
   * ISO date-time string for the start of the window (e.g. 15 days ago) and we
   * ask EmployeesV2 for everyone whose EmploymentStartDate is within it.
   *
   * @param {string} sinceIso - Inclusive lower bound as an ISO date-time string
   *   (e.g. '2026-07-07T00:00:00Z').
   * @returns {Promise} Axios response with employee data
   */
  async getNewJoinersSince(sinceIso) {
    console.log(`🔍 Fetching new joiners since: ${sinceIso}`);

    const params = {
      $filter: `EmploymentStartDate ge ${sinceIso}`,
      $orderby: 'EmploymentStartDate desc',
    };

    // Reuse getEmployees so we inherit its cross-company fallback + 400 handling.
    return this.getEmployees(params);
  },

  /**
   * Get employees whose Employment End Date falls within a date window.
   *
   * Used by the Manager Dashboard "Exiting employees" metric. The caller passes
   * an ISO window [fromIso, toIso] (e.g. today → 30 days from now) and we ask
   * EmployeesV2 for everyone whose EmploymentEndDate falls inside it.
   *
   * @param {string} fromIso - Inclusive lower bound as an ISO date-time string
   *   (e.g. '2026-07-22T00:00:00Z').
   * @param {string} toIso - Inclusive upper bound as an ISO date-time string
   *   (e.g. '2026-08-21T23:59:59Z').
   * @returns {Promise} Axios response with employee data
   */
  async getExitingEmployeesBetween(fromIso, toIso) {
    console.log(`🔍 Fetching exiting employees between: ${fromIso} and ${toIso}`);

    const params = {
      $filter: `EmploymentEndDate ge ${fromIso} and EmploymentEndDate le ${toIso}`,
      $orderby: 'EmploymentEndDate asc',
    };

    // Reuse getEmployees so we inherit its cross-company fallback + 400 handling.
    return this.getEmployees(params);
  },

  /**
   * Get employee by email
   * @param {string} email - Employee email address
   * @returns {Promise} Axios response with employee data
   */
  async getEmployeeByEmail(email) {
    console.log(`🔍 Fetching employee by email: ${email}`);

    // First try without cross-company to avoid SQL subquery errors
    try {
      const params = {
        $filter: `PrimaryContactEmail eq '${email.toLowerCase()}'`,
        'cross-origin': true,
      };

      const response = await Api.get('/data/EmployeesV2', { params });

      if (response.data && response.data.value && response.data.value.length > 0) {
        console.log('✅ Employee found');
        return response;
      }

      console.log('⚠️ No employee found for email:', email);
      return response;
    } catch (error) {
      // If we get a 400 error (SQL subquery issue), return empty result
      if (error.response && error.response.status === 400) {
        console.error(
          '❌ 400 Error - SQL subquery issue, returning empty result. Response body:',
          error.response.data
        );
        return { data: { value: [] } };
      }
      throw error;
    }
  },

  /**
   * Search employees by name
   * @param {string} searchTerm - Search term for employee name
   * @returns {Promise} Axios response with employee data
   */
  async searchEmployees(searchTerm) {
    const params = {
      $filter: `contains(displayName, '${searchTerm}') or contains(fullName, '${searchTerm}')`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/EmployeesV2', { params });
  },

  /**
   * Get employees by department
   * @param {string} departmentId - Department ID
   * @returns {Promise} Axios response with employee data
   */
  async getEmployeesByDepartment(departmentId) {
    const params = {
      $filter: `DepartmentId eq '${departmentId}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/EmployeesV2', { params });
  },

  /**
   * Update employee information
   * @param {string} personnelNumber - Employee personnel number
   * @param {Object} data - Employee data to update (must include EmploymentLegalEntityId and EmploymentId)
   * @returns {Promise} Axios response
   */
  async updateEmployee(personnelNumber, data) {
    console.log(`📝 Updating employee: ${personnelNumber}`, data);

    const legalEntityId = data.EmploymentLegalEntityId || 'USMF';
    const employmentId = data.EmploymentId;

    // Remove key fields from update payload
    const updateData = { ...data };
    delete updateData.EmploymentLegalEntityId;
    delete updateData.EmploymentId;

    // OData requires specific headers
    const headers = {
      'Content-Type': 'application/json',
      'If-Match': '*',
    };

    // Use EmployeesV2 with PersonnelNumber, EmploymentLegalEntityId, and EmploymentId
    const url = `/data/EmployeesV2(PersonnelNumber='${personnelNumber}',EmploymentLegalEntityId='${legalEntityId}',EmploymentId='${employmentId}')`;
    console.log('📤 Updating via EmployeesV2:', url);

    try {
      const response = await Api.patch(url, updateData, {
        headers,
        params: { 'cross-company': true }
      });
      console.log('✅ Employee updated successfully via EmployeesV2');
      return response;
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || error.response?.data?.Message || error.message;
      console.error(`❌ EmployeesV2 PATCH failed (${error.response?.status}):`, errorMsg);
      throw new Error(`Unable to update employee. Error: ${errorMsg}`);
    }
  },

  /**
   * Update employee field
   * @param {string} personnelNumber - Employee personnel number
   * @param {string} legalEntityId - Legal entity ID (company)
   * @param {string} fieldName - Field name to update
   * @param {any} value - New value
   * @returns {Promise} Axios response
   */
  async updateEmployeeField(personnelNumber, legalEntityId, fieldName, value) {
    const data = {
      [fieldName]: value,
    };

    return this.updateEmployee(personnelNumber, { ...data, EmploymentLegalEntityId: legalEntityId });
  },

  /**
   * Get worker bank accounts by personnel number
   * @param {string} personnelNumber - Employee personnel number
   * @returns {Promise} Axios response with bank accounts data
   */
  async getWorkerBankAccounts(personnelNumber) {
    console.log(`🔍 Fetching WorkerBankAccounts for: ${personnelNumber}`);
    return await Api.get('/data/WorkerBankAccounts', { params: { $filter: `PersonnelNumber eq '${personnelNumber}'`, 'cross-company': true } });
  },

  /**
   * Update worker bank account
   * @param {string} personnelNumber - Employee personnel number
   * @param {string} accountIdentification - Account identification key
   * @param {Object} data - Bank account fields to update
   * @returns {Promise} Axios response
   */
  async updateWorkerBankAccount(personnelNumber, accountIdentification, data) {
    console.log(`📝 Updating WorkerBankAccount: ${personnelNumber}`, data);
    const url = `/data/WorkerBankAccounts(PersonnelNumber='${personnelNumber}',AccountIdentification='${accountIdentification}')`;
    return await Api.patch(url, data, {
      headers: { 'Content-Type': 'application/json', 'If-Match': '*' },
      params: { 'cross-company': true },
    });
  },

  /**
   * Get personal contact people
   * @returns {Promise} Axios response with personal contact people data
   */
  async getPersonalContactPeople(personnelNumber) {
    console.log(`🔍 Fetching PersonalContactPeople for: ${personnelNumber}`);
    return await Api.get('/data/PersonalContactPeople', { params: { $filter: `WorkerPersonnelNumber eq '${personnelNumber}'`, 'cross-company': true } });
  },

  /**
   * Update personal contact person
   * @param {string} workerPersonnelNumber - Worker personnel number
   * @param {string} contactPersonPartyNumber - Contact person party number
   * @param {Object} data - Fields to update
   * @returns {Promise} Axios response
   */
  async createPersonalContactPerson(data) {
    console.log('📝 Creating PersonalContactPerson', data);
    return await Api.post('/data/PersonalContactPeople', data, {
      headers: { 'Content-Type': 'application/json' },
      params: { 'cross-company': true },
    });
  },

  async updatePersonalContactPerson(workerPersonnelNumber, contactPersonPartyNumber, relationshipType, relationshipValidFrom, data) {
    console.log(`📝 Updating PersonalContactPerson: ${contactPersonPartyNumber}`, data);
    const url = `/data/PersonalContactPeople(WorkerPersonnelNumber='${workerPersonnelNumber}',ContactPersonPartyNumber='${contactPersonPartyNumber}',RelationshipType='${relationshipType}',RelationshipValidFrom=${relationshipValidFrom})`;
    return await Api.patch(url, data, {
      headers: { 'Content-Type': 'application/json', 'If-Match': '*' },
      params: { 'cross-company': true },
    });
  },

  /**
   * Get party location postal addresses
   * @returns {Promise} Axios response with postal addresses data
   */
  async createPartyLocationPostalAddress(data) {
    const payload = { ...data, IsPostalAddress: 'Yes' };
    console.log('📝 Creating PartyLocationPostalAddress', payload);
    return await Api.post('/data/PartyLocationPostalAddresses', payload, {
      headers: { 'Content-Type': 'application/json' },
      params: { 'cross-company': true },
    });
  },

  async updatePartyLocationPostalAddress(partyNumber, locationId, validFrom, data) {
    const payload = { ...data, IsPostalAddress: 'Yes' };
    console.log(`📝 Updating PartyLocationPostalAddress: ${partyNumber}/${locationId}`, payload);
    const url = `/data/PartyLocationPostalAddresses(PartyNumber='${partyNumber}',LocationId='${locationId}',ValidFrom=${validFrom})`;
    return await Api.patch(url, payload, {
      headers: { 'Content-Type': 'application/json', 'If-Match': '*' },
      params: { 'cross-company': true },
    });
  },

  async getPartyLocationPostalAddresses(partyNumber) {
    console.log(`🔍 Fetching PartyLocationPostalAddresses for PartyNumber: ${partyNumber}`);
    return await Api.get('/data/PartyLocationPostalAddresses', { params: { $filter: `PartyNumber eq '${partyNumber}'`, 'cross-company': true } });
  },

  async deletePersonalContactPerson(workerPersonnelNumber, contactPersonPartyNumber, relationshipType, relationshipValidFrom) {
    console.log(`🗑️ Deleting PersonalContactPerson: ${contactPersonPartyNumber}`);
    const url = `/data/PersonalContactPeople(WorkerPersonnelNumber='${workerPersonnelNumber}',ContactPersonPartyNumber='${contactPersonPartyNumber}',RelationshipType='${relationshipType}',RelationshipValidFrom=${relationshipValidFrom})`;
    return await Api.delete(url, {
      headers: { 'If-Match': '*' },
      params: { 'cross-company': true },
    });
  },

  async deletePartyLocationPostalAddress(partyNumber, locationId, validFrom) {
    console.log(`🗑️ Deleting PartyLocationPostalAddress: ${partyNumber}/${locationId}`);
    const url = `/data/PartyLocationPostalAddresses(PartyNumber='${partyNumber}',LocationId='${locationId}',ValidFrom=${validFrom})`;
    return await Api.delete(url, {
      headers: { 'If-Match': '*' },
      params: { 'cross-company': true },
    });
  },

  /**
   * Get address country regions
   * @returns {Promise} Axios response with country regions data
   */
  async getAddressCountryRegions() {
    console.log('🌍 Fetching address country regions');
    return await Api.get('/data/AddressCountryRegions', {
      params: { 'cross-company': true },
    });
  },
};

export default employeeService;
